import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'astroturf/react'
import { currentUser } from 'js/utils/user';
import { BodyLink } from 'js/design_system/link/BodyLink';
import { Body } from 'js/design_system/Body';
import routes from 'sitewide-js/core_pre_webpack/routes';
import * as api from 'js/social/api';
import ViewsWatcher from 'js/social/shared/ViewsWatcher';
import CreateSocialItem, {
  useCurrentUserAsAuthor,
} from 'js/social/shared/CreateSocialItem';
import {
  Comment as CommentType,
  FollowableUser,
  NewsType,
  ResourceType,
} from 'js/social/shared/types';
import { SocialActionsMenu, useMenuActions } from 'js/social/shared/Menu';
import { useConfirmationModal } from 'js/social/shared/ConfirmationModal';
import {
  BlockIcon,
  DeleteInactive,
  FlagInactive,
} from 'js/social/shared/Icons';
import UnTagOutlineIcon from 'js/svg/colorless/untag_outline.svg';
import AccountRemoveOutlineIcon from 'js/svg/colorless/account_remove_outline.svg';
import { SocialEditor } from 'js/social/shared/SocialEditor/SocialEditor';
import { getAge } from 'js/social/shared/dateUtils';
import useShowToggle from 'js/social/shared/useShowToggle';
import useCommentInteractions, {
  FeedCommentsActions,
} from './useCommentInteractions';
import Base from './Base';
import Cta from './Cta';
import { FeedContext } from '../contexts';
import Header from './Header';
import { Chain } from 'js/social/Comment/types';
import { extractNumericIdFromOpenalexId } from 'js/social/shared/utils';
import _HeaderMenu from "Comment-HeaderMenu.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/Comment/Comment.tsx?styleId=HeaderMenu";
import _CommentContent from "Comment-CommentContent.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/Comment/Comment.tsx?styleId=CommentContent";
import _CommentFooter from "Comment-CommentFooter.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/Comment/Comment.tsx?styleId=CommentFooter";
import _styles from "Comment-styles.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/Comment/Comment.tsx?styleId=styles";
import _ClickToExpand from "Comment-ClickToExpand.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/Comment/Comment.tsx?styleId=ClickToExpand";


const HeaderMenu = /*#__PURE__*/styled("div", null, {
  displayName: "HeaderMenu",
  styles: _HeaderMenu
});

const CommentContent = /*#__PURE__*/styled("div", null, {
  displayName: "CommentContent",
  styles: _CommentContent
});

const CommentFooter = /*#__PURE__*/styled("div", null, {
  displayName: "CommentFooter",
  styles: _CommentFooter
});

const styles = _styles;

const ClickToExpand = /*#__PURE__*/styled("button", null, {
  displayName: "ClickToExpand",
  styles: _ClickToExpand
});

type Props = CommentType &
  FeedCommentsActions & {
    className?: string;
    chain?: Chain;
    newsType: NewsType;
    commentableId: number | string;
    parentCommentId?: number | string | null;
    /**
     * Each post only has knowledge of user ids, e.g. post owner,
     * commenter.
     * Information about users exists separately and is referenced by not
     * just a single post. This function lets a post access a particular
     * user's info.
     *
     * @param id ID of a user
     * @return A hash containing user data
     */
    getUserData: (id: number) => FollowableUser;
    expand?: () => void;
  };

const Comment: React.FunctionComponent<Props> = ({
  id,
  content,
  tags,
  hashtags,
  userId,
  createdAt,
  reactions,
  replies,
  className,
  chain,
  newsType,
  commentableId,
  parentCommentId = null,
  getUserData,
  expand,
  ...feedActions
}) => {
  const commenter = getUserData(userId);
  const hasReplies = replies && replies.data.length > 0;
  const isReply = parentCommentId !== null;
  const {
    feedConfig: { page },
  } = useContext(FeedContext);
  const defaultShowAllReplies =
    (hasReplies && replies.data.every((reply) => reply.expandByDefault)) ||
    page === 'single_post';
  const {
    isShown: showAllReplies,
    toggle: toggleShowAllReplies,
    update: updateShowAllReplies,
  } = useShowToggle(defaultShowAllReplies);

  const [touchedToggle, setTouchedToggle] = useState(false);

  useEffect(() => {
    if (!touchedToggle) {
      updateShowAllReplies(defaultShowAllReplies);
    }
  }, [defaultShowAllReplies, updateShowAllReplies, touchedToggle]);

  const author = useCurrentUserAsAuthor();
  const communityRouteBase = {
    paper_update: routes.community_show_paper_update_url,
    post: routes.community_show_post_url,
    openalex_work: null,
    work: null,
    paper_set: null,
  }[newsType];

  const {
    isShown: showCreateReplyModal,
    show: openCreateReplyModal,
    hide: closeCreateReplyModal,
  } = useShowToggle();

  const { actions, permissions } = useCommentInteractions({
    newsType,
    commentId: id,
    commentableId,
    parentCommentId,
    userId,
    tags,
    ...feedActions,
  });

  const {
    modal: confirmationModal,
    showModal: showConfirmationModal,
    ...confirmationModalActions
  } = useConfirmationModal();
  const {
    openUnfollowUserConfirmationModal,
    openBlockUserConfirmationModal,
    openReportItemConfirmationModal,
    openDeleteItemConfirmationModal,
    openUntagItemConfirmationModal,
  } = useMenuActions(
    commenter.displayName,
    ResourceType.Comment,
    showConfirmationModal,
    confirmationModalActions,
    {
      ...actions,
      ignoreItem: () => null,
      reportItem: actions.reportComment,
      deleteItem: actions.deleteComment,
      removeTag: actions.removeTag,
    },
  );

  const { recordViews } = useContext(FeedContext);

  const recordCommentView = useCallback(async () => {
    const sendableId =
      newsType === 'openalex_work'
        ? extractNumericIdFromOpenalexId(commentableId)
        : commentableId;
    if (currentUser != null && recordViews) {
      await api.createCommentView({
        userId: currentUser.id,
        commentableId: sendableId,
        commentableType: newsType,
        commentId: id,
      });
    }
  }, [commentableId, id, recordViews, newsType]);

  const headerMenu = (
    <HeaderMenu>
      {communityRouteBase ? (
        <BodyLink
          className={styles.grayBody}
          size="xs"
          href={communityRouteBase({
            [`${newsType}_id`]: commentableId,
            c: id,
          })}
        >
          {getAge(createdAt)}
        </BodyLink>
      ) : (
        <Body size="xs" className={styles.grayBody}>
          {getAge(createdAt)}
        </Body>
      )}
      <SocialActionsMenu
        placement="bottom-end"
        menuItems={[
          {
            showItem: permissions.canUntag,
            onClick: openUntagItemConfirmationModal,
            itemIcon: <UnTagOutlineIcon size={20} />,
            itemText: 'Remove tag',
            itemSubtext: `You won't be tagged in this comment anymore.`,
          },
          {
            showItem: permissions.canFollow && commenter.following,
            onClick: openUnfollowUserConfirmationModal,
            itemIcon: <AccountRemoveOutlineIcon size={20} />,
            itemText: `Unfollow ${commenter.displayName}`,
          },
          {
            showItem: permissions.canBlock,
            onClick: openBlockUserConfirmationModal,
            itemIcon: <BlockIcon width={20} height={20} />,
            itemText: `Block ${commenter.displayName}`,
          },
          {
            showItem: permissions.canReport,
            onClick: openReportItemConfirmationModal,
            itemIcon: <FlagInactive width={20} height={20} />,
            itemText: 'Report comment',
          },
          {
            showItem: permissions.canDelete,
            onClick: openDeleteItemConfirmationModal,
            itemIcon: <DeleteInactive />,
            itemText: 'Delete',
          },
        ]}
      />
    </HeaderMenu>
  );

  const commentContent = (
    <CommentContent>
      <SocialEditor
        readOnly
        content={content}
        tags={tags}
        // Allow all hashtags for rate-limited comments
        allowedHashtags={
          id === 0 ? undefined : hashtags.map((hashtag) => hashtag.hashtag)
        }
        lines={page === 'single_post' ? undefined : 5}
      />
    </CommentContent>
  );

  const commenterWithAffiliation: React.ComponentProps<
    typeof Base
  >['commenter'] = {
    ...commenter,
    affiliation: commenter.university?.displayName,
  };

  const replyList = useMemo(() => {
    const visibleReplies = replies.data.filter(
      (reply) => showAllReplies || (!touchedToggle && reply.expandByDefault),
    );
    return visibleReplies.map((reply, index) => {
      const lastReply = index === visibleReplies.length - 1;
      let chain: Chain = undefined;
      if (!lastReply) {
        chain = 'reply';
      } else if (!showAllReplies) {
        chain = 'more';
      }

      return (
        <Comment
          key={`${reply.id}|${index}`}
          chain={chain}
          newsType={newsType}
          className={styles.reply}
          commentableId={commentableId}
          parentCommentId={id}
          getUserData={getUserData}
          {...reply}
          {...feedActions}
        />
      );
    });
  }, [
    commentableId,
    feedActions,
    getUserData,
    id,
    newsType,
    showAllReplies,
    touchedToggle,
    replies,
  ]);

  const baseChain: Chain = useMemo(() => {
    if (hasReplies) {
      return replyList.length > 0 ? 'expanded' : 'collapsed';
    }
    return undefined;
  }, [hasReplies, replyList]);

  return (
    <Base
      className={className}
      chain={chain ?? baseChain}
      commenter={commenterWithAffiliation}
      commenterUrl={commenter.url}
      header={
        <Header
          commenter={commenterWithAffiliation}
          commenterUrl={commenter.url}
          headerMenu={headerMenu}
        />
      }
      content={
        <ViewsWatcher
          key={content}
          type="comment"
          id={id}
          sendView={recordCommentView}
          threshold={0.9}
        >
          {commentContent}
        </ViewsWatcher>
      }
      footer={
        <CommentFooter>
          <Cta
            id={id}
            liked={reactions.like.reacted}
            likeCount={reactions.like.count}
            replyCount={replies.count}
            likeComment={actions.likeComment}
            unlikeComment={actions.unlikeComment}
            onReplyClick={openCreateReplyModal}
            onReplyCountClick={() => {
              setTouchedToggle(true);
              toggleShowAllReplies();
            }}
          />
        </CommentFooter>
      }
    >
      {showConfirmationModal && confirmationModal}
      {showCreateReplyModal && (
        <CreateSocialItem
          id={`post:${commentableId}:comment:${parentCommentId}:reply`}
          type={ResourceType.Comment}
          author={author}
          heading={`Reply to ${commenter.displayName}`}
          cta="Reply"
          initialContent={isReply ? ' ' : undefined}
          initialTags={
            isReply
              ? [{ location: 0, tagId: userId, tagType: 'user' }]
              : undefined
          }
          onClose={closeCreateReplyModal}
          onSubmit={(payload) => {
            actions.createReply(payload);
            if (expand) expand();
          }}
        >
          <Base
            commenter={commenterWithAffiliation}
            header={<Header commenter={commenterWithAffiliation} />}
            content={commentContent}
          />
        </CreateSocialItem>
      )}
      {hasReplies && (
        <>
          <div>{replyList}</div>
          {!showAllReplies && (
            <div style={{ float: 'left' }}>
              <ClickToExpand
                onClick={() => {
                  setTouchedToggle(true);
                  toggleShowAllReplies();
                }}
              >
                Show {replyList.length > 0 ? 'more' : ''} replies
              </ClickToExpand>
            </div>
          )}
        </>
      )}
    </Base>
  );
};

export default Comment;
